/**
 * connectionSharing — the client write-path for D2 live sharing (dev + behind
 * NEW_EXPERIENCE until the flip). The READ path is the get_shared_items resolver
 * (migration 046); this is the lifecycle that keeps the two sharing tables
 * correct when the holder's OWN contact slots change.
 *
 * Sharing is keyed by the contact SLOT (item_key = phone, email, email2, …), so
 * when a slot's value changes IN PLACE nothing here runs — the resolver just
 * delivers the new value (live sharing). Only a delete or a field promotion
 * moves a slot, and then the sharing must move with it.
 */
import { supabase } from '../supabase';
import { isNewExperience, isLiveSharingReadOn } from '../../utils/newExperience';
import { ALL_CONTACT_KEYS } from '../../utils/contactKeys';
import { getProfileUserId } from '../../utils/identityUtils';
import { SOURCE_BRANCH } from '../../utils/deployInfo';
import { logConnectionError } from '../analytics/eventLogger';

// Profile fields whose D2 item_key differs from the field name.
const PROFILE_FIELD_TO_ITEM = { currentCity: 'livesIn', aboutMe: 'aboutMe' };

/**
 * The D2 item keys a save patch CLEARS (value empty/absent): a contact slot by
 * its own key, plus currentCity → 'livesIn' and aboutMe → 'aboutMe'. Non-sharing
 * fields (name, photoUrl, …) are ignored. The own-profile save path feeds each
 * to applyContactSlotChange so a cleared slot's sharing is dropped (rule 11) and
 * a re-added detail starts unshared. Pure.
 * @param {object} patch
 * @returns {string[]} the cleared D2 item keys
 */
export const clearedShareItemsFromPatch = (patch = {}) =>
  Object.entries(patch)
    .filter(([, v]) => v === '' || v === null || v === undefined)
    .map(([k]) => (ALL_CONTACT_KEYS.includes(k) ? k : PROFILE_FIELD_TO_ITEM[k]))
    .filter(Boolean);

/**
 * getSharedItems — the client READ of the live-sharing resolver (migration 046
 * get_shared_items). One RPC per People/chat load returns every item the viewer
 * may currently see across all their active connections, with the holder's LIVE
 * value and the group source. The read path groups these by room and
 * wholesale-replaces the cached items (connectionName.applySharedItemsToAnonData).
 *
 * Returns a map { [chatRoomId]: [{ itemKey, value, sourceGroupIds }] } on a
 * successful read — a room ABSENT from the map means the holder currently shares
 * nothing with this viewer, so that room's live-shared items are cleared.
 * Returns NULL when the resolver could not be read (flag off, offline, or RPC
 * error): the caller then leaves the offline cache untouched instead of wiping
 * it. That null-vs-empty distinction is the offline guard — empty = "shares
 * nothing", null = "don't know, trust the cache".
 *
 * @param {string} profileUserId
 * @returns {Promise<Object<string,Array>|null>}
 */
export const getSharedItems = async (profileUserId) => {
  // Read switch (off until the backfill) — off → null, so the read path leaves
  // the cache untouched and falls back to the pre-D2 read.
  if (!isLiveSharingReadOn() || !profileUserId || !supabase?.rpc) return null;
  const { data, error } = await supabase.rpc('get_shared_items', { p_user_id: profileUserId });
  if (error) {
    console.error('[SHARING] get_shared_items failed:', error.message);
    return null;
  }
  const byRoom = {};
  for (const row of data || []) {
    if (!byRoom[row.chat_room_id]) byRoom[row.chat_room_id] = [];
    byRoom[row.chat_room_id].push({
      itemKey: row.item_key,
      value: row.value,
      sourceGroupIds: row.source_group_ids || [],
    });
  }
  return byRoom;
};

/**
 * applyContactSlotChange — persist the sharing lifecycle when the holder deletes
 * a contact slot or ContactFields promotes a secondary into a freed slot
 * (phone2 → phone; same email/instagram/whatsapp). Mirrors
 * applyContactDeletion.js over BOTH sharing tables:
 *   1. CLEAR the deleted slot's sharing (rule 11 — a re-added slot starts
 *      unshared; nothing survives for a value that's gone).
 *   2. If a secondary was promoted, RE-KEY its sharing onto the freed slot, so
 *      the promoted value keeps its own audience and never inherits the deleted
 *      detail's (the leak guard — Mark 2026-10-14).
 *
 * Owner-RLS scopes every write to my own rows, so no owner filter is needed.
 * Call this BEFORE saving the new profile value (safe ordering: sharing is
 * re-keyed before the value moves, so no window exposes the wrong value).
 * Dormant unless NEW_EXPERIENCE (branch deploy / post-flip).
 *
 * @param {{deletedKey:string, promotedFromKey?:(string|null)}} change
 */
export const applyContactSlotChange = async ({ deletedKey, promotedFromKey = null } = {}) => {
  if (!isNewExperience() || !deletedKey) return;

  // 1. clear the deleted slot's sharing in both tables (RLS → my rows only).
  //    The two tables are independent, so clear them concurrently.
  await Promise.all([
    supabase.from('group_share_sets').delete().eq('item_key', deletedKey),
    supabase.from('connection_item_shares').delete().eq('item_key', deletedKey),
  ]);

  // 2. the promoted slot's sharing carries onto the freed slot (clears above run
  //    first, so no PK clash: the target key's rows are already gone)
  if (promotedFromKey) {
    await Promise.all([
      supabase
        .from('group_share_sets')
        .update({ item_key: deletedKey })
        .eq('item_key', promotedFromKey),
      supabase
        .from('connection_item_shares')
        .update({ item_key: deletedKey })
        .eq('item_key', promotedFromKey),
    ]);
  }
};

/**
 * recordShare — the one writer for a share's choice (migration 049
 * record_share; plan step 4). Records WHAT was chosen for this person, server
 * side, so every device of the account agrees: Full profile, a Custom set
 * (ticked 'on', unticked 'off'), a group (adds them to it, never removes), or
 * the remembered last Custom set. Delivery is unchanged — the share's message
 * still goes out through commitSharedFields; this records the choice the
 * share sheet (rule 7's `Share more`, rule 18) and the flip's live sharing read.
 *
 * Dormant unless NEW_EXPERIENCE. A failure is logged, never thrown: the share
 * itself already went out.
 *
 * @param {Object} p
 * @param {string} p.chatRoomId
 * @param {'full'|'custom'|'last_custom'|'group'} p.kind
 * @param {string[]} [p.on] - item keys shared (custom / last_custom)
 * @param {string[]} [p.off] - item keys deliberately not shared (custom / last_custom)
 * @param {string|null} [p.groupId] - the group (group)
 * @param {boolean} [p.exchange=true] - from the exchange sheet: stamps the group's
 *   recency and the account's last Custom set; Detail's sheet passes false
 * @returns {Promise<boolean>} recorded
 */
export const recordShare = async ({
  chatRoomId,
  kind,
  on = [],
  off = [],
  groupId = null,
  exchange = true,
}) => {
  if (!isNewExperience() || !chatRoomId || !kind) return false;
  const profileUserId = getProfileUserId();
  if (!profileUserId) return false;
  const { error } = await supabase.rpc('record_share', {
    p_profile_user_id: profileUserId,
    p_chat_room_id: chatRoomId,
    p_kind: kind,
    p_on: on,
    p_off: off,
    p_group_id: groupId,
    p_exchange: exchange,
    p_source_branch: SOURCE_BRANCH,
  });
  if (error) {
    logConnectionError('record_share_failed', { chatRoomId, kind, error: error.message });
    return false;
  }
  return true;
};
